import React, { CSSProperties, useEffect, useMemo, useRef, useState } from "react"

import { Box, Flex, Switch, Text, ToastId, useToast } from "@chakra-ui/react"
import { AppSelect, AppSelectOption } from "@/widgets/Common"
import { VideoUsecase } from "@/usecases/video"
import cameraOffIcon from "@/assest/ic_camera_off.svg"
import cameraOnIcon from "@/assest/ic_camera_on.svg"

interface VideoContentProps {
  triggerStyle?: CSSProperties
}

// 清洗摄像头名称：浏览器返回的 label 常带 VID:PID 等参数，如
// "USB Camera VID:1133 PID:2075" 或 "HD Camera (046d:0825)"，只保留名称部分
const formatCameraLabel = (label: string): string => {
  // 去掉末尾括号参数，如 " (046d:0825)"
  const parenEnd = label.lastIndexOf(")")
  if (parenEnd !== -1) {
    const parenStart = label.lastIndexOf("(", parenEnd)
    if (parenStart !== -1) {
      label = label.slice(0, parenStart) + label.slice(parenEnd + 1)
    }
  }
  // 去掉 VID:xxxx / PID:xxxx 片段
  const tokens = label.split(/\s+/).filter((token) => {
    const upper = token.toUpperCase()
    return !upper.startsWith("VID:") && !upper.startsWith("PID:")
  })
  return tokens.join(" ").trim() || "摄像头"
}

const VideoContent: React.FC<VideoContentProps> = ({ triggerStyle }) => {
  // 是否启用摄像头
  const [isVideoInputEnable, setVideoInputEnable] = useState<boolean>(
    VideoUsecase.isVideoInputEnabled()
  )

  const [hasVideoDev, setHasVideoDev] = useState<boolean>(true)
  const [isVideoInputOperating, setVideoInputOperating] =
    useState<boolean>(false)
  // 摄像头设备列表与当前选中项，仅在多摄像头时显示切换下拉框
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("")
  // 对齐分辨率选择器：用 AppSelect 渲染摄像头设备下拉；label 仅显示名称，去掉 VID/PID 等参数
  const cameraOptions = useMemo<AppSelectOption[]>(
    () =>
      videoDevices.map((device) => ({
        label: formatCameraLabel(device.label),
        value: device.deviceId,
      })),
    [videoDevices]
  )
  const selectedCameraOption = useMemo<AppSelectOption | undefined>(
    () => cameraOptions.find((option) => option.value === selectedDeviceId),
    [cameraOptions, selectedDeviceId]
  )
  const toast = useToast()
  const cameraToastId = useRef<ToastId | null>(null)

  const showCameraLoading = (title: string) => {
    if (
      cameraToastId.current !== null &&
      toast.isActive(cameraToastId.current)
    ) {
      toast.close(cameraToastId.current)
    }

    cameraToastId.current = toast({
      title,
      status: "loading",
      duration: null,
      isClosable: true,
      position: "top-right",
      containerStyle: {
        width: "190px",
      },
    })
  }

  const showCameraResult = (
    title: string,
    status: "success" | "error" = "success",
    icon?: React.ReactNode
  ) => {
    const options = {
      title,
      status,
      icon,
      duration: 2000,
      isClosable: true,
      position: "top-right" as const,
      containerStyle: {
        width: "190px",
      },
    }

    if (
      cameraToastId.current !== null &&
      toast.isActive(cameraToastId.current)
    ) {
      toast.update(cameraToastId.current, options)
      return
    }

    cameraToastId.current = toast(options)
  }

  const waitForLoadingVisible = async (startedAt: number) => {
    const minLoadingMs = 500
    const elapsedMs = Date.now() - startedAt
    if (elapsedMs >= minLoadingMs) {
      return
    }

    await new Promise((resolve) => {
      window.setTimeout(resolve, minLoadingMs - elapsedMs)
    })
  }

  useEffect(
    () => VideoUsecase.addVideoInputStateListener(setVideoInputEnable),
    []
  )

  useEffect(() => {
    const refreshVideoDevices = async () => {
      const devices = await VideoUsecase.getVideoInputDevices()
      const list = devices ?? []
      setVideoDevices(list)
      const current = VideoUsecase.getCurrentVideoInputDeviceId()
      setSelectedDeviceId(current ?? list[0]?.deviceId ?? "")
      return devices
    }

    const init = async () => {
      // 获取摄像头设备
      const devices = await refreshVideoDevices()

      if (devices !== null && devices.length > 0) {
        setHasVideoDev(true)

        if (
          VideoUsecase.isVideoInputEnabled() &&
          VideoUsecase.getVideoInputStream() === null
        ) {
          const isOpen = await VideoUsecase.openVideoInputStream()
          setVideoInputEnable(isOpen)
          // 开启后回填当前实际使用的 deviceId
          setSelectedDeviceId(
            VideoUsecase.getCurrentVideoInputDeviceId() ??
              (await VideoUsecase.getVideoInputDevices())?.[0]?.deviceId ??
              ""
          )
        }
      } else {
        setHasVideoDev(false)
        setVideoInputEnable(false)
        VideoUsecase.closeVideoInputStream()
      }
    }

    init().catch((error) => {
      console.error("Error initializing video device:", error)
      setHasVideoDev(false)
      setVideoInputEnable(false)
    })
    const handleVideoDeviceChange = () => {
      // 设备列表变化时校验当前摄像头是否被拔出（devicechange 路上报）
      void VideoUsecase.verifyActiveCameraAvailable()
      // 刷新设备下拉框列表与选中项
      void refreshVideoDevices()
      init().catch((error) => {
        console.error("Error handling video device change:", error)
      })
    }
    navigator.mediaDevices?.addEventListener?.(
      "devicechange",
      handleVideoDeviceChange
    )

    return () => {
      navigator.mediaDevices?.removeEventListener?.(
        "devicechange",
        handleVideoDeviceChange
      )
    }
  }, [])

  const handleVideoInputEnableChange = async (enable: boolean) => {
    if (isVideoInputOperating) {
      return
    }

    setVideoInputOperating(true)
    try {
      if (enable) {
        // 打开摄像头输入流
        const startedAt = Date.now()
        showCameraLoading("正在打开摄像头")
        const isOpen = await VideoUsecase.openVideoInputStream()
        await waitForLoadingVisible(startedAt)
        setVideoInputEnable(isOpen)
        // 开启后回填实际使用的 deviceId
        setSelectedDeviceId(
          VideoUsecase.getCurrentVideoInputDeviceId() ?? selectedDeviceId
        )
        showCameraResult(
          isOpen ? "摄像头已开启" : "摄像头开启失败",
          isOpen ? "success" : "error",
          isOpen ? (
            <img src={cameraOnIcon} alt="" width="20" height="20" />
          ) : undefined
        )
      } else {
        // 关闭摄像头输入流
        const startedAt = Date.now()
        showCameraLoading("正在关闭摄像头")
        await VideoUsecase.closeVideoInputStream()
        await waitForLoadingVisible(startedAt)
        setVideoInputEnable(false)
        showCameraResult(
          "摄像头已关闭",
          "success",
          <img src={cameraOffIcon} alt="" width="20" height="20" />
        )
      }
    } catch (error) {
      console.error("Error changing video input state:", error)
      showCameraResult(enable ? "摄像头开启失败" : "摄像头关闭失败", "error")
    } finally {
      setVideoInputOperating(false)
    }
  }

  // 热切换摄像头设备：复用 SDK 已保留的 sender，走 replaceTrack 无需 SDP 协商
  const handleSwitchCamera = async (deviceId: string) => {
    if (isVideoInputOperating || deviceId === selectedDeviceId) {
      return
    }
    setVideoInputOperating(true)
    try {
      const startedAt = Date.now()
      showCameraLoading("正在切换摄像头")
      const success = await VideoUsecase.switchCamera(deviceId)
      await waitForLoadingVisible(startedAt)
      if (success) {
        setSelectedDeviceId(deviceId)
        showCameraResult("摄像头已切换", "success")
      } else {
        showCameraResult("摄像头切换失败", "error")
      }
    } catch (error) {
      console.error("Error switching camera:", error)
      showCameraResult("摄像头切换失败", "error")
    } finally {
      setVideoInputOperating(false)
    }
  }

  return (
    <Flex direction="column">
      {/* 对齐 android：摄像头选择下拉框在左、开关在右，同一行 */}
      <Flex
        direction={"row"}
        justifyContent={"space-between"}
        alignItems="center"
        minHeight="34px"
        marginTop="1px"
        paddingTop="3px"
        borderTop="1px solid #ffffff33"
      >
        <Text color="#FFFFFF" fontSize="13px" flexShrink={0}>
          摄像头
        </Text>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flex: "1 1 0",
            minWidth: 0,
            justifyContent: "flex-end",
            gap: "6px",
          }}
        >
          {hasVideoDev && videoDevices.length > 1 ? (
            <Box flex="1 1 0" minWidth={0} maxWidth="185px">
              <AppSelect
                value={selectedCameraOption}
                options={cameraOptions}
                triggerStyle={triggerStyle}
                onChange={(option) => {
                  handleSwitchCamera(option.value as string)
                }}
              />
            </Box>
          ) : !hasVideoDev ? (
            <span
              style={{
                backgroundColor: "gray",
                color: "white",
                fontSize: "12px",
                padding: "3px 7px 3px 7px",
                borderRadius: "3px",
              }}
            >
              暂无可用的摄像头
            </span>
          ) : null}
          <Switch
            isChecked={isVideoInputEnable}
            isDisabled={!hasVideoDev || isVideoInputOperating}
            flexShrink={0}
            onChange={(event) => {
              handleVideoInputEnableChange(event.target.checked)
            }}
          />
        </div>
      </Flex>
    </Flex>
  )
}

export default VideoContent
